import { adminRoutes } from '@bettermode/domain/routes/web/admin'
import { isClientError } from '@tribeplatform/gql-client/lib'
import {
  type BillingPrice,
  type BillingProduct,
} from '@tribeplatform/gql-client/types'
import { useDomain } from '@tribeplatform/react-components/common/hooks'
import { useI18n } from '@tribeplatform/react-components/i18n'
import {
  useCreateSubscriptionCheckoutSession,
  useUpdateSubscriptionInterval,
  useUpdateSubscriptionPlan,
} from '@tribeplatform/react-sdk/hooks'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useTrackCheckoutStarted } from '../hooks/useTrackCheckoutStarted.js'
import { CheckoutStartedQueryParams } from '../types.js'

interface Props {
  currentProductOrder: number
  newProduct: BillingProduct
  price: BillingPrice
}

export const usePlanCardButtonActions = ({
  currentProductOrder,
  newProduct,
  price,
}: Props) => {
  const { $t } = useI18n()
  const { trackCheckoutStarted } = useTrackCheckoutStarted()
  const { mutateAsync: createSession, isLoading: isCreatingSession } =
    useCreateSubscriptionCheckoutSession()
  const {
    mutateAsync: updateSubscriptionPlan,
    isLoading: isUpdatingSubscriptionPlan,
  } = useUpdateSubscriptionPlan()
  const {
    mutateAsync: updateSubscriptionInterval,
    isLoading: isUpdatingInterval,
  } = useUpdateSubscriptionInterval()
  const { getResourceUrl } = useDomain()

  const isLoading =
    isCreatingSession || isUpdatingSubscriptionPlan || isUpdatingInterval

  const showPlanUpdatedToast = () => {
    toast({
      title: $t({
        defaultMessage: 'Plan updated',
        id: 'Admin.Billing.ChangePlan.Success',
      }),
      description: $t({
        defaultMessage:
          'Your plan is updated. Changes may take a few moments to appear in your account.',
        id: 'Admin.Billing.ChangePlan.SuccessDescription',
      }),
      status: 'success',
    })
  }

  const showChangePlanErrorToast = () => {
    toast({
      title: $t({
        defaultMessage: 'Something went wrong',
        id: 'Admin.Billing.ChangePlan.Error',
      }),
      description: $t({
        defaultMessage: 'Unable to change plan',
        id: 'Admin.Billing.ChangePlan.ErrorDescription',
      }),
      status: 'error',
    })
  }

  const switchBillingInterval = async (options?: { sendToast?: boolean }) => {
    const { sendToast = true } = options ?? {}

    try {
      await updateSubscriptionInterval({
        interval: price.interval,
      })

      if (sendToast) {
        showPlanUpdatedToast()
      }
    } catch {
      showChangePlanErrorToast()
    }
  }

  const openCheckout = async (
    promotionCode?: string,
    options?: { sameTab?: boolean },
  ): Promise<{ promoError?: string }> => {
    // Native setup-mode conversion returns via `success_url`, so it redirects the current tab —
    // no pop-up, and no blank-tab flash when a typed promotion code is rejected. Legacy
    // subscription-mode Checkout opens a new tab, pre-opened before the await so a pop-up
    // blocker cannot catch the redirect.
    const { sameTab = false } = options ?? {}
    const newWindow = sameTab ? null : window.open()
    // `window.open()` returns null under a pop-up blocker or enterprise policy. Bail with a
    // clear message rather than letting the later redirect throw (which would reject the
    // promise, leaving the button spinning with no toast and no inline error).
    if (!sameTab && !newWindow) {
      toast({
        title: $t({
          defaultMessage: 'Pop-up blocked',
          id: 'Admin.Billing.Checkout.PopupBlockedTitle',
        }),
        description: $t({
          defaultMessage: 'Allow pop-ups for this site, then try again.',
          id: 'Admin.Billing.Checkout.PopupBlockedDescription',
        }),
        status: 'error',
      })
      return {}
    }
    const planId = newProduct.id
    const planName = newProduct.name
    const planAmount = price.amount
    const planInterval = price.interval

    const checkoutQs: CheckoutStartedQueryParams = {
      planId,
      planName,
      planAmount: `${planAmount}`,
      planInterval,
      success: 'true',
    }

    const queryParams = new URLSearchParams({ ...checkoutQs })

    const successUrl = getResourceUrl(
      `${adminRoutes.billing.root}?sessionId={CHECKOUT_SESSION_ID}&${queryParams.toString()}`,
    )
    const cancelUrl = getResourceUrl(
      `${adminRoutes.billing.plans}?success=false&sessionId={CHECKOUT_SESSION_ID}`,
    )

    try {
      const session = await createSession({
        priceId: price.id,
        successUrl,
        cancelUrl,
        ...(promotionCode ? { promotionCode } : {}),
      })
      // Fire-and-forget, and only once a session exists: a rejected promotion code is not a
      // started checkout. The redirect must never wait on telemetry — the blank tab from
      // `window.open()` above only gets its URL here, so nothing is awaited before it.
      trackCheckoutStarted({
        planId,
        planName,
        planAmount,
        planInterval,
      })
      if (newWindow) {
        newWindow.location.href = session.url
      } else {
        window.location.assign(session.url)
      }
      return {}
    } catch (e) {
      newWindow?.close()
      // An invalid promotion code is the customer's to fix inline, under the field — not a
      // toast. Everything else is a genuine failure.
      const message = isClientError(e)
        ? e.response.errors[0]?.message
        : undefined
      if (message?.toLowerCase().includes('promotion code')) {
        return { promoError: message }
      }
      // The API's own message is the useful one when it has one — e.g. the fail-closed
      // "This subscription can't be changed here. Please contact support." for a hand-configured
      // trial — and it is written for the customer. Fall back to the generic copy otherwise.
      toast({
        title: $t({
          defaultMessage: 'Something went wrong',
          id: 'Admin.Billing.BillingPortal.Error',
        }),
        description:
          message ||
          $t({
            defaultMessage: 'Unable to open billing portal',
            id: 'Admin.Billing.BillingPortal.ErrorDescription',
          }),
        status: 'error',
      })
      return {}
    }
  }

  const autoUpdatePlan = async () => {
    const newWindow = window.open()

    try {
      await updateSubscriptionPlan({
        priceId: price.id,
      })

      const queryParams = new URLSearchParams()

      if (newProduct.order > currentProductOrder) {
        queryParams.append('upgrade', 'true')
      }

      newWindow.location.href = `${adminRoutes.billing.root}?${queryParams.toString()}`
      showPlanUpdatedToast()
    } catch {
      newWindow.close()
      showChangePlanErrorToast()
    }
  }

  return {
    autoUpdatePlan,
    isLoading,
    openCheckout,
    switchBillingInterval,
  }
}
